import { useEffect, useState, useMemo, useRef } from 'react';
import { User, Session, AuthError, AuthChangeEvent } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase, currentSupabaseEnv } from '@/lib/supabase';
import { cache } from '@/lib/cache';
import { clearStoredViewport } from '@/components/map/hooks/useUrlState';
import { trackLogout, resetUser } from '@/lib/analytics';
import { clearEnergyTimeSeriesCache } from '@/hooks/useEnergyTimeSeries';
import { AuthContext, type AuthContextType } from './AuthContext';

/**
 * Provides authentication state and actions (user, session, loading, signIn, signUp, signOut) to descendant components via AuthContext.
 *
 * Initializes session from Supabase, subscribes to auth state changes, and keeps context in sync.
 * The signIn implementation detects common network/CORS failures and returns a descriptive error object when those occur;
 * successful sign-ins also attempt to notify a backend endpoint for tracking.
 *
 * @param children - React node(s) that will receive the authentication context
 * @returns The AuthContext provider element that wraps `children`
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Track the current user ID to prevent unnecessary re-renders on token refresh
  // When Supabase refreshes the JWT token on window focus, onAuthStateChange fires
  // but the user hasn't actually changed - only the token has
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Get initial session - with error handling for stale/invalid tokens
    supabase.auth.getSession()
      .then(({ data: { session } }: { data: { session: Session | null } }) => {
        currentUserIdRef.current = session?.user?.id ?? null;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch((error) => {
        // If getSession fails (network error, invalid token, etc), clear state and let user re-login
        console.warn('[AuthContext] Failed to get session, clearing auth state:', error);
        // Clear any stale Supabase auth tokens from localStorage
        try {
          const authKeys = Object.keys(localStorage).filter(key => key.startsWith('sb-') && key.includes('-auth-'));
          authKeys.forEach(key => localStorage.removeItem(key));
        } catch (storageError) {
          console.warn('[AuthContext] Failed to clear stale auth tokens:', storageError);
        }
        currentUserIdRef.current = null;
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, newSession: Session | null) => {
      const newUserId = newSession?.user?.id ?? null;

      // Only update state if:
      // 1. User actually changed (sign in/out, different user)
      // 2. Initial load (currentUserIdRef is null and we have a session)
      // Skip updates for TOKEN_REFRESHED events where user ID is the same
      // This prevents cascading re-renders across all context consumers
      if (event === 'TOKEN_REFRESHED' && currentUserIdRef.current === newUserId) {
        // Token refreshed but user unchanged - update session silently without triggering re-renders
        // We still update session ref for token access, but don't call setSession
        return;
      }

      // Session expiry / remote revocation emits SIGNED_OUT without going
      // through our signOut() wrapper — the user-agnostic time-series cache
      // must still be invalidated or the next user inherits it for its TTL.
      if (event === 'SIGNED_OUT' || (currentUserIdRef.current && newUserId && currentUserIdRef.current !== newUserId)) {
        clearEnergyTimeSeriesCache();
      }

      currentUserIdRef.current = newUserId;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      // Check if the error is a CORS/network error
      // When CORS blocks a request, Supabase may return an error or throw an exception
      if (error) {
        const errorMessage = String(error.message || '').toLowerCase();
        const errorName = String((error as { name?: string }).name || '').toLowerCase();
        const errorStatus = (error as { status?: number }).status;
        const errorCode = (error as { code?: string }).code || '';

        // Known Supabase auth error codes (these are NOT CORS errors)
        const authErrorCodes = ['invalid_credentials', 'email_not_confirmed', 'user_not_found', 'too_many_requests'];
        const isAuthError = authErrorCodes.includes(errorCode);

        // Check for CORS/network errors in various formats
        // Network errors often have status 0, null, undefined, or no status
        const isNetworkError =
          !isAuthError && (
            errorMessage.includes('failed to fetch') ||
            errorMessage.includes('cors') ||
            errorMessage.includes('network') ||
            errorMessage.includes('err_failed') ||
            errorMessage.includes('typeerror') ||
            errorName === 'typeerror' ||
            errorName === 'corserror' ||
            errorName === 'networkerror' ||
            errorStatus === 0 ||
            (errorStatus === null && errorMessage === '') ||
            (errorStatus === undefined && errorMessage === '')
          );

        if (isNetworkError) {
          const corsError = {
            name: 'CorsError',
            message: 'CORS error: Please add http://localhost:5173 to your Supabase project\'s allowed origins. Go to Project Settings → API → Allowed Origins.',
          } as AuthError;
          return { error: corsError };
        }

        // Report genuine server-side failures (e.g. the GoTrue 500
        // "Database error querying schema" seen on demo) to Sentry. supabase-js
        // returns these as values rather than throwing, so they bypass the
        // ErrorBoundary and Sentry's global handlers and would otherwise be
        // invisible. Network/CORS (handled above) and expected auth errors
        // (bad password, unconfirmed email, etc.) are excluded to avoid noise.
        const isServerError =
          (typeof errorStatus === 'number' && errorStatus >= 500) || errorCode === 'unexpected_failure';
        if (isServerError) {
          Sentry.captureException(error, {
            tags: { area: 'auth', operation: 'signInWithPassword', supabaseEnv: currentSupabaseEnv },
            extra: { status: errorStatus, code: errorCode },
          });
        }

          // Log failed auth attempt for security audit (fire-and-forget)
          if (isAuthError) {
            try {
              fetch('/api/v1/auth/failed-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, reason: errorCode || error.message }),
              }).catch(() => {}); // Silently ignore logging failures
            } catch {
              // Ignore logging errors
            }
          }
      }

      // If sign-in was successful, notify backend for security tracking
      if (!error && data.session) {
        // CRITICAL: Clear stale cache on sign-in to ensure fresh RBAC data
        // This prevents issues where users had empty companies/groups cached
        // from before their memberships were assigned
        try {
          await cache.clear();
          console.log('[AuthContext] IndexedDB cache cleared on signIn');
        } catch (cacheError) {
          console.warn('[AuthContext] Failed to clear cache on signIn:', cacheError);
        }

        try {
          // Use relative path to match api.ts pattern (works in dev via proxy, prod via Vercel rewrites)
          await fetch('/api/v1/auth/signin-event', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${data.session.access_token}`,
              'Content-Type': 'application/json',
              'X-Supabase-Environment': currentSupabaseEnv,
            },
          });
        } catch (notificationError) {
          // Don't fail the sign-in if notification fails
          console.warn('Failed to send sign-in notification:', notificationError);
        }
      }

      return { error };
    } catch (err: unknown) {
      // Catch network errors (including CORS) that are thrown as exceptions
      // When CORS blocks a request, browsers throw TypeError: Failed to fetch
      const errorMessage = String((err as Error)?.message || '').toLowerCase();
      const errorName = String((err as Error)?.name || '').toLowerCase();
      const errorStack = String((err as Error)?.stack || '').toLowerCase();

      // Check for CORS/network errors - be very permissive since CORS errors
      // can manifest in different ways depending on browser and Supabase client version
      const isNetworkError =
        errorMessage.includes('failed to fetch') ||
        errorMessage.includes('cors') ||
        errorMessage.includes('network') ||
        errorMessage.includes('err_failed') ||
        errorName === 'typeerror' ||
        errorName === 'networkerror' ||
        errorStack.includes('failed to fetch') ||
        errorStack.includes('cors');

      if (isNetworkError) {
        // Create an error object that matches AuthError structure
        const corsError = {
          name: 'CorsError',
          message: 'CORS error: Please add http://localhost:5173 to your Supabase project\'s allowed origins. Go to Project Settings → API → Allowed Origins.',
        } as AuthError;
        return { error: corsError };
      }
      // Re-throw other errors (these are unexpected)
      console.error('Unexpected error during sign in:', err);
      throw err;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: Record<string, unknown>
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    return { error };
  };

  const signOut = async () => {
    // CRITICAL: Clear all cached data on logout to prevent cross-user data leakage
    // This ensures the next user gets fresh data from their own RBAC context

    // 1. Clear IndexedDB cache (used by api.ts staleWhileRevalidate)
    try {
      await cache.clear();
      console.log('[AuthContext] IndexedDB cache cleared on signOut');
    } catch (error) {
      console.warn('[AuthContext] Failed to clear IndexedDB cache on signOut:', error);
    }

    // 2. CRITICAL: Clear FilterContext localStorage cache
    // Without this, User B sees User A's cached companys/groups on shared devices!
    // Clear both old user-agnostic keys AND user-specific keys for complete cleanup.
    try {
      // Old user-agnostic keys (legacy cleanup)
      const legacyFilterCacheKeys = [
        'company_filter_companies',
        'company_filter_groups',
        'company_filter_selected_companies',
        'company_filter_selected_groups',
        'company_filter_selected_models',
        'company_filter_selected_statuses',
        'company_filter_timestamp',
      ];
      legacyFilterCacheKeys.forEach(key => localStorage.removeItem(key));

      // Also clear any user-specific keys (new format: base_userId)
      // Iterate through all localStorage keys and remove any company_filter_* keys
      const allKeys = Object.keys(localStorage);
      const companyFilterKeys = allKeys.filter(key => key.startsWith('company_filter_'));
      companyFilterKeys.forEach(key => localStorage.removeItem(key));

      // Clear reports_* keys (Reports page filters) - ensures filters reset on re-login
      const reportsKeys = allKeys.filter(key => key.startsWith('reports_'));
      reportsKeys.forEach(key => localStorage.removeItem(key));

      console.log('[AuthContext] Filter cache cleared on signOut:', {
        companyFilterKeys: companyFilterKeys.length,
        reportsKeys: reportsKeys.length
      });
    } catch (error) {
      console.warn('[AuthContext] Failed to clear localStorage cache on signOut:', error);
    }

    // 3. Clear map viewport from sessionStorage
    try {
      clearStoredViewport();
      console.log('[AuthContext] Map viewport cleared on signOut');
    } catch (error) {
      console.warn('[AuthContext] Failed to clear map viewport on signOut:', error);
    }

    // 3b. Clear the energy time-series module cache — keyed by query params
    // only, so a surviving entry could serve one user's results (incl.
    // unassigned-sentinel data) to the next user within the cache TTL.
    try {
      clearEnergyTimeSeriesCache();
    } catch (error) {
      console.warn('[AuthContext] Failed to clear energy time-series cache on signOut:', error);
    }

    // 4. Notify backend of signout for session tracking (fire-and-forget)
    try {
      const token = session?.access_token;
      if (token) {
        await fetch('/api/v1/auth/signout-event', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('[AuthContext] Signout event tracked');
      }
    } catch (error) {
      console.warn('[AuthContext] Failed to track signout:', error);
    }

    // 5. Reset PostHog identity so next user gets a clean session
    trackLogout();
    resetUser();

    // 'local': App, Pocket and Sales share this Supabase project, and the default
    // ('global') revokes every session the user has, on every device (T-152).
    await supabase.auth.signOut({ scope: 'local' });
  };

  // Memoize context value to prevent unnecessary re-renders of consumers
  // Only re-create value when actual auth state changes
  const value = useMemo(() => ({
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  }), [user, session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
